import { patchState, signalStoreFeature } from '@ngrx/signals';
import {
  Dispatcher,
  EventInstance,
  Events,
  withEventHandlers,
} from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { concatMap, delay, exhaustMap, filter, switchMap, tap } from 'rxjs';

import { TasksService } from '../../../services';
import { TASK_EVENTS } from '../../signal-events';
import {
  NON_CANCELLED_TASK_STATUSES,
  TaskInstance,
  TaskType,
} from '@data-access/tasks/types';
import {
  DefaultTaskListState,
  EXTERNAL_TODO_TASK_TYPES,
  REQUIRED_DOCUMENT_TASKS,
} from '@data-access/tasks/constants';
import { categorizeRemainingTasks } from '@data-access/tasks/utils/task-list.utils';
import { extractBlobAndFilename, saveAs } from '@shared/utils/download.util';

function fetchPatchEvent(
  categorizedTasks: Record<string, TaskInstance[]>,
  dispatcher: Dispatcher,
) {
  const _taskEvents: EventInstance<string, unknown>[] = [];
  for (const [key, value] of Object.entries(categorizedTasks)) {
    if (value.length > 0) {
      switch (key) {
        case 'document':
          _taskEvents.push(
            TASK_EVENTS.patchDocumentTaskList({ taskList: value }),
          );
          break;
        case 'section':
          _taskEvents.push(
            TASK_EVENTS.patchSectionTaskList({ taskList: value }),
          );
          break;
        case 'question':
          _taskEvents.push(
            TASK_EVENTS.patchQuestionTaskList({ taskList: value }),
          );
          break;
        default:
          console.warn(`Unknown task type: ${key}`);
          break;
      }
    }
  }
  return _taskEvents.length > 0
    ? _taskEvents.forEach((event) => dispatcher.dispatch(event))
    : false;
}

export const withTaskEffects = (
  _events: Events,
  _taskListService: TasksService,
  _dispatcher: Dispatcher,
) =>
  signalStoreFeature(
    withEventHandlers(({ ...store }) => ({
      /*
       * Effect that listens for the getTaskList event and fetches the main task list
       * */
      bulkUpdateTaskStatus$: _events.on(TASK_EVENTS.bulkUpdateTaskStatus).pipe(
        tap(() =>
          patchState(store, {
            error: null,
            isSMEQCLoading: true,
            loading: true,
          }),
        ),
        concatMap((event) => {
          const { workspaceId, taskInstanceIds, taskStatus, taskListType } =
            event.payload;
          return _taskListService
            .bulkUpdateTaskStatus(workspaceId, {
              task_instance_ids: taskInstanceIds,
              to_status: taskStatus,
            })
            .pipe(
              mapResponse({
                error: (error: Error) => {
                  patchState(store, {
                    error,
                    isSMEQCLoading: false,
                    loading: false,
                  });
                },
                next: () => {
                  patchState(store, { loading: false });
                  // Refetch all task types to update the UI
                  _dispatcher.dispatch(
                    TASK_EVENTS.getRemainingToDoTaskCount({
                      workspaceID: workspaceId,
                    }),
                  );
                  _dispatcher.dispatch(
                    TASK_EVENTS.getSMEQCTaskListByIds({
                      isCompleted: undefined,
                      taskType: ['qc_mark_required', 'sme_mark_required'],
                      ids: [...taskInstanceIds],
                      taskListType,
                      workspaceId: workspaceId,
                    }),
                  );
                },
              }),
            );
        }),
      ),
      exportAllThreads$: _events.on(TASK_EVENTS.exportAllThreads).pipe(
        switchMap((event) => {
          const { workspaceID } = event.payload;
          return _taskListService.exportAllThreads(workspaceID).pipe(
            filter(Boolean),
            mapResponse({
              // A failed export used to be silent. Surface it on the store like
              // the single-thread export effect does, so the UI can react
              // instead of the download simply never arriving (ERM-12936).
              error: (error: Error) => patchState(store, { error }),
              next: (response) => {
                const extracted = extractBlobAndFilename(response);
                const { blob, filename } = extracted;
                // Saves the file using the extracted blob and filename
                saveAs(blob, filename);
              },
            }),
          );
        }),
      ),

      /*
       * switchMap, not exhaustMap: this is a refresh, so the last dispatch has
       * to win. Under exhaustMap a dispatch that landed while the previous
       * fetch was in flight was dropped, and that in-flight response predated
       * whatever triggered the second one, so the counts sat stale (ERM-12595).
       * */
      getRemainingToDoTaskCount$: _events
        .on(TASK_EVENTS.getRemainingToDoTaskCount)
        .pipe(
          tap(() => patchState(store, { error: null, loading: true })),
          switchMap((event) => {
            const { workspaceID } = event.payload;
            return _taskListService
              .getTaskList(workspaceID, {
                is_internal: false,
                status: [...NON_CANCELLED_TASK_STATUSES],
                // The one list of customer to-do types, shared with the
                // predicate that counts them, so the two cannot drift.
                task_types: [...EXTERNAL_TODO_TASK_TYPES],
              })
              .pipe(
                filter(Boolean),
                mapResponse({
                  error: (error: Error) =>
                    patchState(store, { error, loading: false }),
                  next: (response) => {
                    const categorizedTasks = categorizeRemainingTasks(response);
                    patchState(store, {
                      loading: false,
                      tasksAlreadyLoaded: true,
                      remainingToDoTasks: { ...categorizedTasks },
                    });
                  },
                }),
              );
          }),
        ),
      getSMEQCTaskList$: _events.on(TASK_EVENTS.getSMEQCTaskList).pipe(
        tap(() => patchState(store, { error: null, loading: true })),
        exhaustMap((event) => {
          const { workspaceId, taskType, isCompleted } = event.payload;
          return _taskListService
            .getTaskList(workspaceId, {
              is_completed: isCompleted,
              status: [...NON_CANCELLED_TASK_STATUSES],
              task_types: taskType,
            })
            .pipe(
              filter(Boolean),
              mapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error,
                    isSMEQCLoading: false,
                    loading: false,
                  }),
                next: (response) => {
                  const categorizedTasks = categorizeRemainingTasks(response);
                  patchState(store, {
                    isSMEQCLoading: false,
                    loading: false,
                    tasksAlreadyLoaded: true,
                    remainingSMEQCTasks: { ...categorizedTasks },
                  });
                  return event.payload.skipPatchingSMEQC
                    ? false
                    : fetchPatchEvent(categorizedTasks, _dispatcher);
                },
              }),
            );
        }),
      ),
      getSMEQCTaskListByIds$: _events
        .on(TASK_EVENTS.getSMEQCTaskListByIds)
        .pipe(
          tap(() => patchState(store, { error: null, loading: true })),
          exhaustMap((event) => {
            const { workspaceId, taskType, isCompleted, ids, taskListType } =
              event.payload;
            return _taskListService
              .getTaskListByIds(workspaceId, {
                task_types: taskType,
                ids,
                is_completed: isCompleted,
                status: [...NON_CANCELLED_TASK_STATUSES],
              })
              .pipe(
                mapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      error,
                      isSMEQCLoading: false,
                      loading: false,
                    }),
                  next: (response) => {
                    const _ids = response.map((task) => task.id);
                    const _isCompleted = response.every(
                      (task) => task.status === 'review_completed',
                    );
                    patchState(store, {
                      error: null,
                      loading: false,
                      isSMEQCLoading: false,
                    });
                    return TASK_EVENTS.bulkUpdateTaskStatusByIds({
                      taskIds: _ids,
                      isCompleted: _isCompleted,
                      taskListType: taskListType!,
                    });
                  },
                }),
              );
          }),
        ),
      /* switchMap for the same reason as getRemainingToDoTaskCount$ above: a
       * refresh dispatched while one is in flight must not be dropped. */
      getTaskList$: _events.on(TASK_EVENTS.getTaskList).pipe(
        tap(() =>
          patchState(store, {
            error: null,
            loading: true,
            taskListError: null,
            taskListLoading: true,
          }),
        ),
        switchMap((event) => {
          const { workspaceID, submissionStage } = event.payload;
          const task_types: TaskType[] = [];
          if (submissionStage === 'draft') {
            task_types.push(...REQUIRED_DOCUMENT_TASKS);
          }
          return _taskListService
            .getTaskList(workspaceID, {
              status: [...NON_CANCELLED_TASK_STATUSES],
              task_types,
            })
            .pipe(
              filter(Boolean),
              mapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error,
                    loading: false,
                    taskListError: error,
                    taskListLoading: false,
                  }),
                next: (response) =>
                  TASK_EVENTS.taskListLoaded({
                    taskList: [...response],
                  }),
              }),
            );
        }),
      ),
      /** Effect that listens for the updateTaskListFiltered event and updates the filtered state */
      updateTaskListFiltered$: _events
        .on(TASK_EVENTS.updateTaskListFiltered)
        .pipe(
          tap((event) => {
            const { filtered } = event.payload;
            patchState(store, { filtered });
          }),
        ),
      updateTaskStatus$: _events.on(TASK_EVENTS.updateTaskStatus).pipe(
        tap(() =>
          patchState(store, {
            error: null,
            isSMEQCLoading: true,
            loading: true,
          }),
        ),
        concatMap((event) => {
          const { workspaceId, taskInstanceId, taskStatus, taskListType } =
            event.payload;
          return _taskListService
            .updateTaskStatus(workspaceId, taskInstanceId, {
              status: taskStatus,
            })
            .pipe(
              delay(200),
              mapResponse({
                error: (error: Error) => {
                  patchState(store, {
                    error,
                    isSMEQCLoading: false,
                    loading: false,
                  });
                  /*
                   * @NOTE: HANDLING NEGATIVE OR ERROR STATE BY SETTING is_completed TO FALSE
                   * */
                  const _status =
                    taskStatus === 'review_completed'
                      ? 're_opened'
                      : 'review_completed';
                  const _task = {
                    is_completed: taskStatus === _status,
                  } as TaskInstance;
                  return TASK_EVENTS.updateTaskInList({
                    hasError: true,
                    task: _task,
                    taskInstanceId: taskInstanceId,
                    taskListType,
                  });
                },
                next: (response) => {
                  patchState(store, {
                    error: null,
                    isSMEQCLoading: false,
                    loading: false,
                  });
                  return TASK_EVENTS.updateTaskInList({
                    task: response,
                    taskInstanceId: taskInstanceId,
                    taskListType,
                  });
                },
              }),
            );
        }),
      ),
      resetTaskState$: _events
        .on(TASK_EVENTS.resetTaskListState)
        .pipe(tap(() => patchState(store, { ...DefaultTaskListState }))),
    })),
  );
